// src/components/WhatIDoSection.styles.ts
//
// Home "Engineering capabilities" section.
//
// Interaction model differs by viewport, so only one of the two layouts is
// rendered at a time (see useMediaQuery in the component):
//
//   >= 1024px  a vertical index that drives a single detail panel
//   <  1024px  a stacked accordion
//
// Deliberately no skill meters, percentage ratings, or per-item icons. The
// list is numbered and the detail is prose, because the claim being made is
// about responsibility, not about a score.
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { minDevice } from '../styles/breakpoints';

// Headings and body copy inherit the global sans stack, as the About page
// sections do. Only the labels, indices and technology runs are set in mono.
const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

export const CapabilitiesRoot = styled.section`
  position: relative;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.background};
  padding: 4.5rem 0;

  @media ${minDevice.laptop} {
    padding: 6rem 0;
  }
`;

export const CapabilitiesContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
`;

export const Header = styled(motion.header)`
  margin-bottom: 2.75rem;
`;

export const Eyebrow = styled(motion.p)`
  margin: 0 0 1rem;
  font-family: ${monoStack};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const Heading = styled(motion.h2)`
  margin: 0;
  font-size: 1.6rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: ${({ theme }) => theme.headingColor};

  @media ${minDevice.tablet} {
    font-size: 2rem;
  }
`;

export const Lede = styled(motion.p)`
  margin: 1.25rem 0 0;
  max-width: 58ch;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* ------------------------------------------------------- desktop layout */

export const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);

  @media ${minDevice.laptop} {
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
    gap: 3.5rem;
    align-items: start;
  }
`;

export const IndexList = styled.div`
  display: flex;
  flex-direction: column;
`;

/* A 2px rule on the left acts as the progress indicator: the active row's
   segment brightens, so the current item is legible without a filled card. */
export const IndexButton = styled.button<{ $active: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: baseline;
  gap: 1rem;
  padding: 1.05rem 0 1.05rem 1.25rem;
  background: none;
  border: 0;
  text-align: left;
  cursor: pointer;
  color: ${({ theme, $active }) => ($active ? theme.headingColor : theme.text)};
  transition: color 0.3s ease, padding-left 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${({ theme, $active }) => ($active ? theme.accent : theme.borderColor)};
    transition: background 0.3s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.headingColor};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const IndexNumber = styled.span<{ $active: boolean }>`
  flex: 0 0 auto;
  font-family: ${monoStack};
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme, $active }) => ($active ? theme.accent : theme.borderColor)};
  transition: color 0.3s ease;
`;

export const IndexTitle = styled.span`
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.35;
`;

export const PrimaryTag = styled.span`
  display: inline-block;
  margin-left: 0.6rem;
  padding: 0.15rem 0.45rem;
  vertical-align: middle;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.6rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
  white-space: nowrap;
`;

/* -------------------------------------------------------- detail panel */

export const DetailPanel = styled(motion.div)`
  margin-top: 2.5rem;
  padding: 1.75rem;
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};

  @media ${minDevice.laptop} {
    margin-top: 0;
    position: sticky;
    top: 5.5rem;
  }
`;

export const DetailIndex = styled.p`
  margin: 0 0 0.6rem;
  font-family: ${monoStack};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.accent};
`;

export const DetailTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.headingColor};

  @media ${minDevice.tablet} {
    font-size: 1.5rem;
  }
`;

export const DetailSummary = styled.p`
  margin: 0.9rem 0 0;
  max-width: 54ch;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.text};
`;

export const DetailMeta = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const MetaLabel = styled.p`
  margin: 0 0 0.5rem;
  font-family: ${monoStack};
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

/* Comma-separated inline run rather than chips: a chip per technology turns
   the section back into the tag cloud it is meant to replace. */
export const MetaRun = styled.p`
  margin: 0;
  font-family: ${monoStack};
  font-size: 0.82rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.secondary};

  span + span::before {
    content: ' / ';
    color: ${({ theme }) => theme.borderColor};
  }
`;

/* ------------------------------------------------------ mobile accordion */

export const Accordion = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AccordionItem = styled.div`
  border-top: 1px solid ${({ theme }) => theme.borderColor};

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  }
`;

export const AccordionButton = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: baseline;
  gap: 0.9rem;
  padding: 1.15rem 0;
  background: none;
  border: 0;
  text-align: left;
  cursor: pointer;
  color: ${({ theme, $open }) => ($open ? theme.headingColor : theme.text)};
`;

export const AccordionChevron = styled(motion.span)`
  margin-left: auto;
  flex: 0 0 auto;
  font-family: ${monoStack};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.accent};
`;

export const AccordionBody = styled(motion.div)`
  overflow: hidden;
`;

export const AccordionInner = styled.div`
  padding: 0 0 1.5rem;
  color: ${({ theme }) => theme.text};
`;
